import { Topic } from '@lemon8/webapp-seo-shared';
import { Link } from '@remix-run/react';
import { FC, useMemo, useCallback } from 'react';
import { StepScroll } from '../step-scroll';
import { TopicArticleCard } from './topic-article-card';
import NumberIcon from '@lemon8/design/lib/icons/icon_assets/number_fill.svg';
import { useAtomValue } from 'jotai';
import { $Locale } from '@lemon8/webapp-shared/model';
import { useTranslation } from 'react-i18next';
import { useNumFormatter } from '~/utils/format/num-formatter';
import { ClickPosition } from '~/utils/click-position';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';
import { $PageLevelTraffic } from 'shared/atoms';
import { EPageType } from 'shared/models';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { useScopedAtomValue } from '@lemon8/webapp-shared';
import { $CurrentPageModule } from '~/models/current-page-module';
import { useViewTracking } from '~/utils/use-view-tracking';

import './style.scss';

const MIN_HASHTAG_ARTICLE_COUNT = 3;
const MAX_HASHTAG_ARTICLE_COUNT = 10;

interface TopicCardProps {
  topic: Topic | string;
}

export const TopicCard: FC<TopicCardProps> = ({ topic }) => {
  const { pageType } = useAtomValue($PageLevelTraffic);
  const isKEPPage = pageType === EPageType.discover;

  const isOnlyWord = typeof topic === 'string';
  const { t } = useTranslation();
  const { region } = useAtomValue($Locale);
  const formatNum = useNumFormatter();
  const active = usePageActive();

  const isMobileUser = useAtomValue($IsMobileUser);
  const currentPageModule = useScopedAtomValue($CurrentPageModule);

  const topicHref = useMemo(() => {
    // TODO 目前只处理 discover 下方的 topic
    if (!isKEPPage) {
      if (isOnlyWord) {
        return `/discover/${topic}?region=${region}`;
      } else {
        return `/discover/${topic.topicName}?region=${region}`;
      }
    }
    if (isOnlyWord) {
      return `/discover/${topic}?region=${region}`;
    }
    return `/topic/${topic.topicId}?region=${region}`;
  }, [topic, region, isKEPPage]);

  const handleHashtagClick = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: ClickPosition.related_hashtag,
        page_click_id: isOnlyWord ? topic : topic.topicId,
      }),
      reflowBehavior: () => {
        if (isOnlyWord) {
          active({
            schemaOption: {
              pageType: 'search',
              query: topic,
            },
            ampExtra: {
              enter_position: ClickPosition.related_hashtag,
            },
          });
        } else {
          active({
            schemaOption: {
              pageType: 'hashtag',
              hashtagId: topic.topicId,
            },
            ampExtra: {
              enter_position: ClickPosition.related_hashtag,
            },
          });
        }
      },
    },
    [active, topic, isOnlyWord],
  );

  const description = useMemo(() => {
    return (
      isKEPPage &&
      !isOnlyWord &&
      t('view_count_plural', { num: topic.viewCount }).replace('%d', formatNum(topic.viewCount))
    );
  }, []);

  const hashtagRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      if (isMobileUser) {
        window.Tea('wap_cta_show', {
          current_page_module: currentPageModule,
          current_position: ClickPosition.related_hashtag,
        });
      }
    }, [isMobileUser]),
  });

  return (
    <Link className="hashtag-card" to={topicHref} draggable={false} onClick={handleHashtagClick} ref={hashtagRef}>
      <div className="hashtag-card-head">
        <div className="hashtag-card-head-icon">
          <NumberIcon />
        </div>
        <div className="hashtag-card-head-main">
          <p className="hashtag-card-head-main-title">{isOnlyWord ? topic : topic.topicName}</p>
          {description && <p className="hashtag-card-head-main-desc">{description}</p>}
        </div>
      </div>
      {!isOnlyWord && topic.articles && topic.articles.length >= MIN_HASHTAG_ARTICLE_COUNT && (
        <StepScroll className="hashtag-card-articles">
          {topic.articles.slice(0, MAX_HASHTAG_ARTICLE_COUNT).map((article) => (
            <TopicArticleCard key={article.groupId} topic={topic} article={article} />
          ))}
        </StepScroll>
      )}
    </Link>
  );
};
